import { formatWhenLine } from '@ecmb/ui';
import { formatEuro } from '../../shared/money-format.js';

/**
 * Display formatting. Euro rendering was the incumbent's en-IE no-decimals
 * style — asserted verbatim by quote-grid/quote-golf/quote-wedding-manual
 * (kept-verbatim ledger; do not change the locale or fraction digits).
 *
 * T549 M3 — a DELIBERATE, NARROW OVERRIDE of that kept-verbatim instruction,
 * stated rather than smuggled. `maximumFractionDigits: 0` did not just drop
 * decimals, it ROUNDED them: a 24.60 deposit was shown to Helen as 25 while the
 * customer's SMS said 24.60 for the same booking (audit A3). Helen cannot answer
 * "why does it say a different number?" off a screen that disagrees with what we
 * sent. The locale and the GROUPING are unchanged, so every whole-euro
 * assertion in the kept-verbatim ledger stays byte-identical; only the
 * fractional case — which had no correct rendering to preserve — moves.
 */
export function euro(cents: number): string {
  return formatEuro(cents);
}

/**
 * Normalize a place/venue name for the PRICE BOOK (T387 M6 ux finding): the
 * queue card shows the customer's verbatim text as evidence, but what lands
 * in rates.location_label is permanent brand copy — "HOWTH," must default to
 * "Howth" in the promote form. Conservative: strips trailing punctuation +
 * collapses whitespace always; re-cases ONLY an all-caps or all-lowercase
 * string (mixed case like "McGrath's Cross" passes through untouched).
 */
export function normalizePlaceName(raw: string): string {
  const cleaned = raw.trim().replace(/[\s,.;:]+$/, '').replace(/\s+/g, ' ');
  if (cleaned === '') return cleaned;
  const isShouting = cleaned === cleaned.toUpperCase();
  const isWhispering = cleaned === cleaned.toLowerCase();
  if (!isShouting && !isWhispering) return cleaned;
  return cleaned
    .toLowerCase()
    .replace(/(^|[\s/-])([a-z])/g, (_m, sep: string, ch: string) => sep + ch.toUpperCase());
}

/** "12 Jul" — provenance/audit dates (T387 M5). Empty string on junk input. */
export function shortDate(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? ''
    : d.toLocaleDateString('en-IE', { day: 'numeric', month: 'short' });
}

/**
 * "Tue, 27 Oct, 09:00": a civil date and a wall-clock time. ⭐ Board 2812 M1 moved
 * its body to `shared/civil-date-label.ts` (the server words the DD-17 mismatch
 * line through it); re-exported here so every client import is unchanged.
 */
export { civilDateLabel } from '../../shared/civil-date-label.js';

/** "today" / "yesterday" / "3 days ago" — the saved-age line on parked
 *  bookings (T409 hygiene: a stale park must AGE visibly, never sink
 *  silently). Whole calendar-day granularity; empty string on junk input or
 *  a future timestamp (clock skew must not render "-1 days ago"). */
export function relativeAge(iso: string, now: Date = new Date()): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const days = Math.floor((now.getTime() - d.getTime()) / 86_400_000);
  if (days < 0) return '';
  if (days === 0) return 'today';
  if (days === 1) return 'yesterday';
  return `${days} days ago`;
}

/** "12 Jul, 14:32" — paid-transition timestamps (T408). Explicit Europe/Dublin
 *  wall time (project timezone rule); empty string on junk input. */
export function shortDateTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const day = d.toLocaleDateString('en-IE', { day: 'numeric', month: 'short', timeZone: 'Europe/Dublin' });
  const time = d.toLocaleTimeString('en-IE', { hour: '2-digit', minute: '2-digit', hour12: false, timeZone: 'Europe/Dublin' });
  return `${day}, ${time}`;
}

/**
 * ⭐ board 2811 M4 (H8): AN INSTANT AS HELEN'S BALANCE SENTENCES SAY IT,
 * `Thu 15 Oct at 10:30`, in Europe/Dublin on a 24-hour clock (CLAUDE.md §2).
 * Built from `formatToParts` rather than a formatted string, so ICU's comma
 * ("Thu, 15 Oct", which `formatDublinInstant` carries) can never reach the copy.
 * '' on junk, `shortDateTime`'s rule.
 */
const DUBLIN_DAY_AT_TIME = new Intl.DateTimeFormat('en-IE', {
  timeZone: 'Europe/Dublin',
  weekday: 'short',
  day: 'numeric',
  month: 'short',
  hour: '2-digit',
  minute: '2-digit',
  hourCycle: 'h23',
});

const DUBLIN_YEAR = new Intl.DateTimeFormat('en-IE', { timeZone: 'Europe/Dublin', year: 'numeric' });

/**
 * ⭐ board 2811 rework part 2c (Captain item 8): THE ONE YEAR RULE FOR A BALANCE
 * DATE. The year is written only when it is not this Dublin year, so a balance
 * date reads like every other date in the app, which never prints the current
 * year, and a mistyped year (D-9: an ask clamped up to four years out) or a
 * booking past New Year shows it: `Thu 15 Oct 2027 at 10:30`.
 */
function dayWords(weekday: string, day: string, month: string, year: string, time: string, nowMs: number): string {
  const thisYear = DUBLIN_YEAR.format(new Date(nowMs));
  return `${weekday} ${day} ${month}${year === thisYear ? '' : ` ${year}`} at ${time}`;
}

export function dublinDayAtTime(iso: string, nowMs: number = Date.now()): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const parts = DUBLIN_DAY_AT_TIME.formatToParts(d);
  const part = (type: Intl.DateTimeFormatPartTypes): string => parts.find((x) => x.type === type)?.value ?? '';
  return dayWords(part('weekday'), part('day'), part('month'), DUBLIN_YEAR.format(d), `${part('hour')}:${part('minute')}`, nowMs);
}

const CIVIL_DAY = new Intl.DateTimeFormat('en-IE', { timeZone: 'UTC', weekday: 'short', day: 'numeric', month: 'short' });

/**
 * ⭐ board 2811 rework part 2c (Captain items 8 and 15): a Dublin WALL date and
 * `HH:MM` (`YYYY-MM-DD`, as `balance_ask_moved`'s stored detail writes them) in
 * `dublinDayAtTime`'s shape and year rule. Read in UTC, `whenText`'s reason: a
 * civil date has no zone to convert. '' on junk, range-checked by round trip.
 */
export function civilDayAtTime(civil: string, time: string, nowMs: number = Date.now()): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(civil);
  if (m === null || !/^\d{2}:\d{2}$/.test(time)) return '';
  const d = new Date(`${civil}T00:00:00Z`);
  if (Number.isNaN(d.getTime()) || d.toISOString().slice(0, 10) !== civil) return '';
  const parts = CIVIL_DAY.formatToParts(d);
  const part = (type: Intl.DateTimeFormatPartTypes): string => parts.find((x) => x.type === type)?.value ?? '';
  return dayWords(part('weekday'), part('day'), part('month'), m[1]!, time, nowMs);
}

/**
 * ⭐ T1723 M5 (M4 gate delta 2) - THE ONE "WHEN" TEXT FOR A JOURNEY'S CIVIL
 * DATE, HOISTED. `Sat 1 Aug, 04:30`, which is DESIGN.md §7's shape.
 *
 * ⛔ THE FINDING, measured live at M2 and fixed on two screens at M4: `Inbox`
 * and `BookingDetail` each joined `journey_date` and `journey_time` RAW and
 * rendered `2026-07-03, 05:30` - an ISO key printed at an operator, on the
 * screens she prices from. M4 then left BYTE-NEAR-IDENTICAL copies of the fix in
 * both files (one comment word apart) and `Bookings` still printing the raw key
 * in its row meta, so the duplication is what let the third surface stay wrong.
 * THREE call sites now: `screens/Inbox.tsx`, `screens/BookingDetail.tsx` and
 * `screens/Bookings.tsx`.
 *
 * ⛔ THE DATE GOES THROUGH `formatWhenLine`; THE TIME IS ECHOED AS GIVEN, and
 * that split is deliberate rather than half a job. `inquiries.journey_time` is
 * `optStr(20)` on the public form (`server/inquiry.ts:488`) and
 * `bookings.journey_time` is a `TEXT` column with no CHECK (`migrations.ts:331`)
 * fed from it, so a customer may well have typed "after the 4 o'clock mass".
 * Running that through a clock formatter would either drop it or dress it up as
 * a time she never stated; echoing it keeps what she actually said, which is the
 * half Helen prices on. The formatter's own `timeLine` needs a real instant and
 * a stated MEANING, and neither a web enquiry nor a booking carries one:
 * `timeMeaning` lives on the PORTAL REQUEST, and attributing the home's answer
 * to a booking's own field is a claim the crossing does not make.
 *
 * ⛔ THE INSTANT IS ASSEMBLED AND READ BACK IN `UTC`, NOT IN THE MODULE'S PINNED
 * DUBLIN. `journey_date` is a CIVIL date with no zone of its own, so there is
 * nothing to convert: reading it back in the zone it was written in is what
 * renders the day the customer picked on every host. Projecting a zoneless value
 * into Dublin (or into the host's zone) is how "3 July" becomes "2 July" for an
 * hour of every summer day. This is the opposite case to `formatDublinInstant`,
 * which renders a real INSTANT and must pin the zone for the same reason.
 */
export function whenText(journeyDate: string | null, journeyTime: string | null): string {
  const day =
    journeyDate === null
      ? null
      // `.slice(0, 10)` absorbs a full ISO timestamp if the column shape ever
      // changes; it is a no-op on the 'YYYY-MM-DD' a DATE column returns.
      : formatWhenLine({ journeyDate: `${journeyDate.slice(0, 10)}T00:00:00Z`, timeZone: 'UTC' }).date;
  return [day, journeyTime].filter(Boolean).join(', ');
}
